/**
 * Alerts Table - Matches your exact data structure
 */

import React, { useMemo, useEffect, useRef } from 'react';
import { Pencil } from 'lucide-react';
import { Alert, AlertFeedback } from '../types';
import { FeedbackForm } from './FeedbackForm';
import { useAuth } from '../../../../context/AuthContext';
import { formatDateDDMonYY, formatTimeHHMM } from '../utils/formatters';

interface AlertsTableProps {
  alerts: Alert[];
  loading: boolean;
  expandedRow: number | null;
  editCache: Record<number, AlertFeedback>;
  savingId: number | null;
  selectedDate: string;
  onStartEdit: (alert: Alert) => void;
  onCancelEdit: (id: number) => void;
  onSaveEdit: (id: number) => void;
  onSetEditField: (id: number, field: keyof AlertFeedback, value: any) => void;
  onFaultCategorySelect?: (faultCategory: string) => void;
}

interface ColumnConfig {
  key: string;
  originalName: string;
  displayName: string;
  dataKey: string;
  render: (alert: Alert) => React.ReactNode;
  visibleForAdmin: boolean;
  visibleForUser: boolean;
}

// Helper function to remove suffixes from column names and replace underscores with spaces
const removeSuffix = (name: string): string => {
  return name
    .replace(/\s*_On UI USER INPUT PAGE\s*/gi, '')
    .replace(/\s*_On UI\s*/gi, '')
    .replace(/\s*\(0 for Platform generated, 1 for User entry\)\s*/gi, '')
    .replace(/\s*\(To be selected from the dropdown\)\s*/gi, '')
    .replace(/\s*\(via Foreignkey\)\s*/gi, '')
    .replace(/_/g, ' ') // Replace all underscores with spaces
    .trim();
};

export const AlertsTable: React.FC<AlertsTableProps> = ({
  alerts,
  loading,
  expandedRow,
  editCache,
  savingId,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onSetEditField,
  onFaultCategorySelect,
}) => {
  const { user } = useAuth();
  const roleName = (user?.roleName || user?.role || '').toLowerCase();
  const isAdmin = user?.isGlobalAdmin ||
    ['admin', 'super_admin', 'system_admin', 'company_admin', 'ho_admin', 'site_admin'].includes(roleName);
  const expandedRowRef = useRef<HTMLTableRowElement>(null);
  const actionButtonRefs = useRef<Record<number, HTMLButtonElement>>({});

  // Define all columns with their configuration
  // Both admin and normal users will see these 7 columns:
  // Date, Time, PlantName, SCB_Name_in_Field, Final_Deviation, Fault_Accuracy, Rectification_Status
  const allColumns: ColumnConfig[] = useMemo(() => [
    {
      key: 'date',
      originalName: 'Date_On UI',
      displayName: removeSuffix('Date_On UI'),
      dataKey: 'alert_date',
      render: (alert) => formatDateDDMonYY(alert.alert_date),
      visibleForAdmin: true,
      visibleForUser: true,
    },
    {
      key: 'time',
      originalName: 'Time_On UI',
      displayName: removeSuffix('Time_On UI'),
      dataKey: 'alert_time',
      render: (alert) => {
        // Prefer alert_time, then from_time, otherwise show '-'
        if (alert.alert_time) {
          return formatTimeHHMM(alert.alert_time);
        } else if (alert.from_time) {
          return formatTimeHHMM(alert.from_time);
        }
        return '-';
      },
      visibleForAdmin: true,
      visibleForUser: true,
    },
    {
      key: 'plantName',
      originalName: 'PlantName (via Foreignkey)_On UI',
      displayName: removeSuffix('PlantName (via Foreignkey)_On UI'),
      dataKey: 'plant_name',
      render: (alert) => alert.plant_name_name || alert.plant_name || alert.plant_name_id || '-',
      visibleForAdmin: true,
      visibleForUser: true,
    },
    {
      key: 'equipment',
      originalName: 'Equipment_On UI',
      displayName: 'EQUIPMENT',
      dataKey: 'EQUIPMENT',
      render: (alert) => alert.EQUIPMENT || alert.itemname || '-',
      visibleForAdmin: true,
      visibleForUser: true,
    },
    {
      key: 'scbName',
      originalName: 'SCB_Name_in_Field_On UI',
      displayName: removeSuffix('SCB_Name_in_Field_On UI'),
      dataKey: 'scb_name_in_field',
      render: (alert) => alert.scb_name_in_field || '-',
      visibleForAdmin: true,
      visibleForUser: true,
    },
    {
      key: 'finalDeviation',
      originalName: 'Final_Deviation_On UI',
      displayName: removeSuffix('Final_Deviation_On UI'),
      dataKey: 'final_deviation',
      render: (alert) => alert.final_deviation?.toFixed(2) || '-',
      visibleForAdmin: true,
      visibleForUser: true,
    },
    {
      key: 'faultAccuracy',
      originalName: 'Fault_Accuracy_On UI',
      displayName: removeSuffix('Fault_Accuracy_On UI'),
      dataKey: 'fault_accuracy',
      render: (alert) => {
        if (!alert.fault_accuracy) return '-';
        const capitalized = alert.fault_accuracy.charAt(0).toUpperCase() + alert.fault_accuracy.slice(1);
        return (
          <span className={`badge ${alert.fault_accuracy === 'true' ? 'status-rectified' : 'status-pending'}`}>
            {capitalized}
          </span>
        );
      },
      visibleForAdmin: true,
      visibleForUser: true,
    },
    {
      key: 'rectificationStatus',
      originalName: 'Rectification_Status_On UI',
      displayName: removeSuffix('Rectification_Status_On UI'),
      dataKey: 'fault_rectification_status',
      render: (alert) => {
        return (
          <span className={`badge status-${alert.fault_rectification_status === 'rectified' ? 'rectified' : 'pending'}`}>
            {alert.fault_rectification_status || alert.rectification_status || 'not rectified'}
          </span>
        );
      },
      visibleForAdmin: true,
      visibleForUser: true,
    },
    {
      key: 'actions',
      originalName: 'Actions',
      displayName: 'Additional Inputs',
      dataKey: 'actions',
      render: (alert) => (
        <button
          ref={(el) => {
            if (el) actionButtonRefs.current[alert.id] = el;
          }}
          className="btn-edit"
          onClick={() => onStartEdit(alert)}
          title="Edit alert"
        >
          <Pencil size={16} className="edit-icon" />
        </button>
      ),
      visibleForAdmin: true,
      visibleForUser: true,
    },
    // Hidden columns - these fields are only available in the popup form
    {
      key: 'faultCategory',
      originalName: 'Fault_Category (To be selected from the dropdown)_On UI USER INPUT PAGE',
      displayName: removeSuffix('Fault_Category (To be selected from the dropdown)_On UI USER INPUT PAGE'),
      dataKey: 'fault_category',
      render: (alert) => alert.fault_category || '-',
      visibleForAdmin: false,
      visibleForUser: false,
    },
    {
      key: 'quantumOfFault',
      originalName: 'Quantum_of_Fault_#_On UI USER INPUT PAGE',
      displayName: removeSuffix('Quantum_of_Fault_#_On UI USER INPUT PAGE'),
      dataKey: 'quantum_of_fault',
      render: (alert) => alert.quantum_of_fault ?? alert.no_of_faulty_strings ?? '-',
      visibleForAdmin: false,
      visibleForUser: false,
    },
    {
      key: 'capacityImpacted',
      originalName: 'Capacity_Impacted_On UI USER INPUT PAGE',
      displayName: removeSuffix('Capacity_Impacted_On UI USER INPUT PAGE'),
      dataKey: 'capacity_impacted',
      render: (alert) => alert.capacity_impacted?.toFixed(2) || '-',
      visibleForAdmin: false,
      visibleForUser: false,
    },
    {
      key: 'assignedTo',
      originalName: 'Assigned_to_On UI USER INPUT PAGE',
      displayName: removeSuffix('Assigned_to_On UI USER INPUT PAGE'),
      dataKey: 'assigned_to',
      render: (alert) => alert.assigned_to_username || alert.assigned_to || alert.assigned_to_id || '-',
      visibleForAdmin: false,
      visibleForUser: false,
    },
    {
      key: 'assignedAt',
      originalName: 'Assigned_at_On UI USER INPUT PAGE',
      displayName: removeSuffix('Assigned_at_On UI USER INPUT PAGE'),
      dataKey: 'assigned_at',
      render: (alert) => alert.assigned_at || '-',
      visibleForAdmin: false,
      visibleForUser: false,
    },
    {
      key: 'resolvedAt',
      originalName: 'Resolved_at_On UI USER INPUT PAGE',
      displayName: removeSuffix('Resolved_at_On UI USER INPUT PAGE'),
      dataKey: 'resolved_at',
      render: (alert) => alert.resolved_at || '-',
      visibleForAdmin: false,
      visibleForUser: false,
    },
    {
      key: 'resolvedBy',
      originalName: 'Resolved_by_On UI USER INPUT PAGE',
      displayName: removeSuffix('Resolved_by_On UI USER INPUT PAGE'),
      dataKey: 'resolved_by',
      render: (alert) => alert.resolved_by_username || alert.resolved_by || alert.resolved_by_id || '-',
      visibleForAdmin: false,
      visibleForUser: false,
    },
    {
      key: 'energyLoss',
      originalName: 'Energy_Loss_On UI USER INPUT PAGE',
      displayName: removeSuffix('Energy_Loss_On UI USER INPUT PAGE'),
      dataKey: 'energy_loss',
      render: (alert) => alert.energy_loss?.toFixed(3) || '-',
      visibleForAdmin: false,
      visibleForUser: false,
    },
    {
      key: 'alertSource',
      originalName: 'Alert_Source (0 for Platform generated, 1 for User entry)',
      displayName: removeSuffix('Alert_Source (0 for Platform generated, 1 for User entry)'),
      dataKey: 'alert_source',
      render: (alert) => {
        if (alert.alert_source === undefined) return '-';
        return alert.alert_source === 0 ? 'Platform Generated' : 'User Entry';
      },
      visibleForAdmin: false,
      visibleForUser: false,
    },
    {
      key: 'comments',
      originalName: 'Comments_On UI USER INPUT PAGE',
      displayName: removeSuffix('Comments_On UI USER INPUT PAGE'),
      dataKey: 'comments',
      render: (alert) => alert.comments || '-',
      visibleForAdmin: false,
      visibleForUser: false,
    },
  ], [onStartEdit]);

  // Filter columns based on user role
  const visibleColumns = useMemo(() => {
    return allColumns.filter(col => isAdmin ? col.visibleForAdmin : col.visibleForUser);
  }, [allColumns, isAdmin]);

  const colSpan = visibleColumns.length;

  // Scroll expanded row into view when it opens, aligning with action button
  useEffect(() => {
    if (expandedRow && expandedRowRef.current) {
      // Find the action button for this alert
      const actionButton = actionButtonRefs.current[expandedRow];
      if (actionButton) {
        // Get the table wrapper (scrollable container)
        const tableWrapper = actionButton.closest('.alerts-table-wrapper');

        if (tableWrapper) {
          // Calculate the position of the action button relative to the wrapper
          const buttonRect = actionButton.getBoundingClientRect();
          const wrapperRect = tableWrapper.getBoundingClientRect();
          const scrollLeft = tableWrapper.scrollLeft;
          const buttonLeft = buttonRect.left - wrapperRect.left + scrollLeft;

          // Scroll the wrapper so the action button is centered horizontally
          const targetScrollLeft = buttonLeft - (wrapperRect.width / 2) + (buttonRect.width / 2);

          tableWrapper.scrollTo({
            left: targetScrollLeft,
            behavior: 'smooth'
          });
        } else {
          // Fallback: scroll the action button into view
          actionButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }

        // Scroll the expanded row into view vertically
        setTimeout(() => {
          expandedRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 300);
      }
    }
  }, [expandedRow]);

  // Always render the table with header, even during loading or when empty
  return (
    <table className="alerts-table">
      <thead>
        <tr>
          {visibleColumns.map(col => (
            <th key={col.key}>{col.displayName}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={colSpan} style={{ textAlign: 'center', padding: '40px' }}>
              <div className="loading-state">
                <div className="spinner-medium">⏳</div>
                <p className="empty-text">Loading alerts...</p>
              </div>
            </td>
          </tr>
        ) : alerts.length === 0 ? (
          <tr>
            <td colSpan={colSpan} style={{ textAlign: 'center', padding: '40px' }}>
              <div className="empty-state">
                <p className="empty-text">No alerts found</p>
                <p className="empty-subtext">No alerts for the selected date</p>
              </div>
            </td>
          </tr>
        ) : (
          alerts.map(alert => (
            <React.Fragment key={alert.id}>
              <tr
                className={`alert-row priority-${alert.priority?.toLowerCase() || 'low'}`}
              >
                {visibleColumns.map(col => (
                  <td key={col.key}>{col.render(alert)}</td>
                ))}
              </tr>
              {expandedRow === alert.id && (
                <tr
                  ref={expandedRow === alert.id ? expandedRowRef : null}
                  className={`expanded-row priority-${alert.priority?.toLowerCase() || 'low'}`}
                >
                  {/* Feedback form spans all columns */}
                  <td colSpan={colSpan} className="expanded-cell">
                    <div className="expanded-content">
                      <FeedbackForm
                        alert={alert}
                        feedback={editCache[alert.id]}
                        onFieldChange={(field, value) => onSetEditField(alert.id, field, value)}
                        onSave={() => onSaveEdit(alert.id)}
                        onCancel={() => onCancelEdit(alert.id)}
                        saving={savingId === alert.id}
                        isAdmin={isAdmin}
                        onFaultCategorySelect={onFaultCategorySelect}
                      />
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))
        )}
      </tbody>
    </table>
  );
};
